import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Globe } from "lucide-react";
import { LogoIcon, LogoText } from "./LogoIcon";
import { useLang } from "@/context/LanguageContext";

function Section({ section }) {
  return (
    <div className="mb-10">
      <h2 className="text-lg font-bold text-brand-orange uppercase tracking-wider mb-4">
        {section.title}
      </h2>
      <div className="space-y-3 text-white/60 text-sm leading-relaxed">
        {section.content.map((block, i) => {
          if (block.type === "paragraph") {
            return <p key={i}>{block.text}</p>;
          }
          if (block.type === "list") {
            return (
              <ul key={i} className="space-y-1 pl-2">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-2">
                    <span className="text-brand-cyan shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            );
          }
          if (block.type === "highlight") {
            return (
              <p key={i}>
                <span className="text-white font-semibold">{block.text}</span>
              </p>
            );
          }
          if (block.type === "email") {
            return (
              <p key={i} className="text-brand-cyan font-medium">{block.text}</p>
            );
          }
          if (block.type === "table") {
            return (
              <div key={i} className="overflow-x-auto mt-4">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      {block.headers.map((h, j) => (
                        <th key={j} className="text-left text-white/40 uppercase tracking-wider py-2 pr-6 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, j) => (
                      <tr key={j} className="border-b border-white/5">
                        {row.map((cell, k) => (
                          <td key={k} className="py-2 pr-6 text-white/50">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLang();
  const privacy = t.privacy;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/5 px-4 sm:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            {privacy.back}
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white border border-white/10 hover:border-brand-cyan/50 rounded transition-all duration-300"
            >
              <Globe size={14} />
              {lang === "es" ? "EN" : "ES"}
            </button>
            <div className="flex items-center gap-2.5">
              <LogoIcon size={28} />
              <LogoText height={20} />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-16">
        <div className="mb-12">
          <p className="text-xs text-brand-cyan uppercase tracking-widest mb-3 font-bold">{privacy.badge}</p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{privacy.title}</h1>
          <p className="text-white/40 text-sm">{privacy.updated}</p>
        </div>

        {privacy.sections.map((section, i) => (
          <Section key={i} section={section} />
        ))}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-4 sm:px-8 py-6">
        <p className="text-center text-white/25 text-xs">
          &copy; {new Date().getFullYear()} Realm Pixel Games. {t.footer.rights}
        </p>
      </footer>
    </div>
  );
}
